/* eslint-disable  @typescript-eslint/no-explicit-any */
import { Component, Host, h, Prop, Watch, State, type EventEmitter, Event, Listen, Element, Method } from '@stencil/core';
import type { FacetsData } from '../../result-page/facets-build-wrapper/facets-build-wrapper';
import type { FilterNode } from '../../result-page/facets-build-wrapper/filter-node';
import type { ChipData } from './filters-header-menu/filters-chips/filters-chips';
import type { FiltersCollapseData } from './filters-header-menu/filters-header-menu';
import {FILTERS_ID_SEPARATOR} from '../../../constants/filterConstants';
import {cloneObjectWithNoReference} from '../../../utils/helpers/clone-object-with-no-reference';
import {FilterChangeContext} from '../../result-page/facets-build-wrapper/models/filter-change-context.enum';
import type { labelProviderInterface } from '../../LabelProviderInterface';

export interface MobileFiltersTranslations {
  'filter': string
  'reset': string
  'show-results': string
  'search-filter': string
}

export type BooleanString = 'true' | 'false';

@Component({
  tag: 'uiaas-search-mobile-filters',
  styleUrl: 'mobile-filters.scss',
  shadow: false,
  scoped: true
})
export class MobileFilters {
  private debounce: any | null = null;
  private headerHeight = 0;
  private readonly filtersHeaderHeight: number = 54;
  private readonly filtersResetHeight: number = 46;
  private readonly filtersResultButtonHeight: number = 72;
  private readonly maxFilterHeight: number = 62;
  private filtersFullMenuElement!: HTMLElement;
  private filtersTreeMenuElement!: HTMLElement;
  private oldScrollY?: number;
  private startScrollObserve = false;
  private canHide = false;
  private onlyCollapse = false;
  @Element() el!: HTMLElement;

  @Prop({mutable: true}) labelProvider?: labelProviderInterface;
  @Prop() readonly rtl: boolean;
  @Prop({ attribute: 'header-selector' }) readonly headerSelector?: string;
  @Prop({ mutable: false, attribute: 'hidden-facets' }) readonly hiddenFacets: string = JSON.stringify([]);
  @Prop({ attribute: 'total-hits' }) readonly totalHitsProp?: string;
  @Prop({attribute: 'translations'}) readonly translationsProp: string = JSON.stringify({
    'filter': 'Filter',
    'reset': 'Reset all',
    'show-results': 'Show results',
    'search-filter': 'Search in filter values'
  });
  @Prop({ attribute: 'facets-data', mutable: true }) facetsDataProp!: string;
  @Prop({ attribute: 'divider-mobile-facets'} ) readonly dividerMobileFacets?: string[];
  @Prop({ attribute: 'hidden-components-height' }) readonly hiddenComponentsHeight?: string;
  @Prop({ attribute: 'scroll-position-for-hide' }) readonly scrollPositionForHide?: string;
  @Prop({attribute: 'show-filters-search-input-limit'}) readonly showFiltersSearchInputLimit: number;
  @Prop() readonly isFlatFacets: boolean = false;
  @Prop() readonly headerAdditionalComponents: {element: (param: boolean) => HTMLElement, elementHeight: number};

  @State() collapsed = false;
  @State() fixedView = false;
  @State() isLoading = false;
  @State() facetsData: FacetsData;

  private translations: MobileFiltersTranslations;

  @Event({
    eventName:'on:chip:remove',
    bubbles: true
  })
    onChipRemove: EventEmitter<ChipData>;

  @Event({
    eventName:'filter:did:change:mobile',
    bubbles: true
  })
    onFilterChangeMobile: EventEmitter<FilterNode>;

  @Event({
    eventName:'filters:did:change:mobile',
    bubbles: true
  })
    onFiltersChangeMobile: EventEmitter<{filterNodes: FilterNode[], context: FilterChangeContext}>;

  @Event({
    eventName:'reset:filters',
    bubbles: false
  })
    reset: EventEmitter<any>;

  @Event({
    eventName:'query',
    bubbles: false
  })
    query: EventEmitter<void>;

  @Event({
    eventName:'clear',
    bubbles: false
  })
    clear: EventEmitter<{ onlyCollapse: boolean, collapseState: boolean }>;

  @Event({
    eventName:'escape',
    bubbles: false
  })
    escape: EventEmitter<void>;

  @Event({
    eventName:'showHeader',
    bubbles: false
  })
    showHeader: EventEmitter<void>;

  @Event({
    eventName: 'hideHeader',
    bubbles: false
  })
    hideHeader: EventEmitter<void>;

  @Event({
    eventName: 'collapsedChange',
    bubbles: false
  })
    collapsedChange: EventEmitter<boolean>;

  @Watch('collapsed')
  onCollapsedChange(newValue: boolean) {
      this.collapsedChange.emit(newValue);
  }

  @Listen('scroll', {
    target: 'window'
  })
  onScroll() {
      if(this.headerSelector && document.body.style.overflow !== 'hidden') {
        if(window.scrollY === 0) {
          this.el.classList.remove('hidden-on-scroll');
          this.fixedView = false;
          this.showHeader.emit();
          this.startScrollObserve = false;
          return;
        }

        if(window.scrollY > 0 && this.countVisibleChips > 0 && !this.fixedView) {
          if(window.scrollY > parseInt(this.hiddenComponentsHeight)) {
            this.el.classList.add('hidden-on-scroll');
            this.fixedView = true;
            this.hideHeader.emit();
            this.startScrollObserve = true;
          }
        }

        if(!this.startScrollObserve) {
          this.startScrollObserve = true;
          return;
        }

        if (this.startScrollObserve) {
          if (this.debounce !== null) {
            clearTimeout(this.debounce);
            this.debounce = null;
          }

          this.debounce = window.setTimeout(() => {
              if (this.oldScrollY > window.scrollY) {
                this.el.classList.remove('hidden-on-scroll');
              } else if(this.canHide) {
                this.el.classList.add('hidden-on-scroll');
              }

              this.oldScrollY = window.scrollY;
              this.debounce = null;
          }, 10);
        }
      }
  }

  @Method()
  async setFixedView(forced: boolean) {
      this.fixedView = forced;
  }

  @Method()
  async setStartScrollObserve(bool: boolean) {
      this.startScrollObserve = bool;
  }

  @Method()
  async setCanHide(bool: boolean) {
      this.canHide = bool;
  }

  @Method()
  async setCollapse(bool: boolean) {
      this.collapsed = bool;
  }

  @Method()
  async clearSelected() {
      (this.filtersTreeMenuElement as any).clearSelected();
  }

  private onResultButtonClick(): void {
      this.query.emit();
      this.collapsed = false;
      this.onlyCollapse = true;
  }

  private onResetButtonClick(): void {
      this.onlyCollapse = true;
      this.reset.emit();
  }

  private onFilterChange(event: CustomEvent) {
      this.onFilterChangeMobile.emit(event.detail);
  }

  private renderAdditionalComponent = () => {
      return !this.collapsed ? this.headerAdditionalComponents.element(this.fixedView) : '';
  };

  get activeResultButton() {
      const previewFacetsState = this.facetsData?.previewsStateData?.facetsChipsState;
      const facetsState = this.facetsData?.facetsChipsState;
      const areObjectsEqual = JSON.stringify(previewFacetsState) === JSON.stringify(facetsState);
      const isButtonActive = previewFacetsState && facetsState && areObjectsEqual;

      return isButtonActive;
  }

  componentWillLoad(): void {
      if (!this.labelProvider) {
        this.labelProvider = (name, fallback) => {
            return fallback;
        };
      }
      this.facetsData = JSON.parse(this.facetsDataProp);
      this.translations = JSON.parse(this.translationsProp);
  }

  componentWillUpdate(): void {
      this.facetsData = JSON.parse(this.facetsDataProp);
      this.translations = JSON.parse(this.translationsProp);
      if(!this.collapsed) {
        this.collapseAllNodes();
      }
  }

  componentDidLoad() {
      if(this.headerSelector) {
        const header: HTMLElement | undefined = document.querySelector(this.headerSelector as string);
        if(header) {
          this.headerHeight = header.offsetHeight;
        }
      }
      this.el.style.top = '0px';
  }

  componentDidRender() {
      this.oldScrollY = parseInt(this.scrollPositionForHide);
      if(this.filtersFullMenuElement && this.fixedView && this.filtersTreeMenuElement) {
        const restFromHeight = (this.facetsData.facetsChipsState.length > 0)
          ? (this.filtersResetHeight + this.filtersHeaderHeight)
          : this.filtersHeaderHeight;
        this.filtersFullMenuElement.style.height = `calc(100vh - ${restFromHeight}px)`;
        this.calculateTreeMenuHeight(restFromHeight);
      } else if (this.filtersFullMenuElement && !this.fixedView && this.filtersTreeMenuElement) {
        this.filtersFullMenuElement.style.height = 'auto';
        this.filtersTreeMenuElement.style.height = 'auto';
      }
  }

  render() {
      return (
        <Host class={`${this.fixedView ? 'fixed' : ''} ${this.collapsed ? 'collapsed' : ''}`}>
          <filters-header-menu
            class={`${(this.collapsed || this.fixedView) ? 'none-static' : 'static'} ${this.fixedView ? 'fixed-view' : ''}`}
            collapsedProp={this.collapsed}
            showChipsProp={this.countVisibleChips > 0 && !this.collapsed}
            onClickFiltersNavbar={(e) => this.onOpenCloseFilters(e)}
          >
            <filters-chips-button-label
              slot="hm-left-button"
              show-chips={(this.countVisibleChips > 0 && !this.collapsed) ? 'true' : 'false'}
              chips-count={`${this.countVisibleChips}`}
              collapsedProp={(this.collapsed) ? 'true' : 'false'}
              onClickOpenFilters={(e) => this.onOpenCloseFilters(e)}
              labelProvider={this.labelProvider}
            ></filters-chips-button-label>
            <filters-chips
              slot="hm-right-chips"
              chipsListProp={(this.isFlatFacets) ? JSON.stringify(this.facetsData.facetLocalChipsState) : JSON.stringify(this.facetsData.facetsChipsState)}
              hiddenFacets={this.hiddenFacets}
              hiddenFirstChildOfFacets={this.dividerMobileFacets ? JSON.stringify(this.dividerMobileFacets) : JSON.stringify([]) }
              onChipRemove={(e) => this.onChipRemove.emit(e.detail)}
            ></filters-chips>
          </filters-header-menu>
          {this.renderAdditionalComponent()}
          <div
            class={`mb-filters-full-menu ${(this.collapsed) ? 'visible d-block' : ''}`}
            ref={(e) => this.filtersFullMenuElement = e}
          >
            <filters-reset
              onSearchReset={() => this.onResetButtonClick()}
              resetLabel={this.translations.reset}
              class={(this.facetsData.facetsState.length > 0) ? 'visible' : ''}
            ></filters-reset>
            <filters-tree-menu
              ref={(e) => this.filtersTreeMenuElement = e}
              filtersTreeProp={(this.isFlatFacets) ? JSON.stringify(this.facetsData.flatFacetsList) : JSON.stringify(this.facetsData.filteredMobileFacets)}
              facetLocalChipsProp={this.facetsData.facetLocalChipsState}
              filterByLocalChipsState={this.isFlatFacets}
              forceCollapse={this.isOnlyOneFacet}
              onFilterChange={(e) => this.onFilterChange(e)}
              onChangeFiltersMobile={(e)=>{
                  this.onFiltersChangeMobile.emit(e.detail);
              }}
              filtersHeaderHeight={this.filtersHeaderHeight}
              filtersResetHeight={this.filtersResetHeight}
              filtersResultButtonHeight={this.filtersResultButtonHeight}
              maxFilterHeight={this.maxFilterHeight}
              searchFilterLabel={this.translations['search-filter']}
              outerFiltersTreeProp={(this.isFlatFacets) ? JSON.stringify([]) : JSON.stringify(this.facetsData.outerMobileFacets)}
              showFiltersSearchInputLimit={this.showFiltersSearchInputLimit}
              labelProvider={this.labelProvider as labelProviderInterface}
              rtl={this.rtl}
              class={{
                'visible d-block' : !!this.facetsData.filteredMobileFacets,
                'has-selected': (this.facetsData.facetsState.length > 0)
              }}
            ></filters-tree-menu>
          </div>
          <result-button
            label={this.translations['show-results']}
            resultCounterProp={(this.facetsData?.totalHits) ? this.facetsData.totalHits.toString() : this.totalHitsProp}
            selectedFiltersExistProp={this.activeResultButton}
            class={this.collapsed
              ? 'visible'
              : ''} onClick:button={(e) => this.onResultButtonClick()}
          ></result-button>
          <loading-spinner visible={this.isLoading ? 'visible' : 'hidden'} label={''}></loading-spinner>
        </Host>
      );
  }

  private get isOnlyOneFacet() {
      return ((this.isFlatFacets) ? this.facetsData.flatFacetsList : this.facetsData.filteredMobileFacets)
        .filter(fc=>fc.children.length > 0)
        .length === 1;
  }

  private get countVisibleChips(): number {
      if(this.isFlatFacets) {
        return this.facetsData.facetLocalChipsState.length;
      }
      const chipsState = this.facetsData.facetsChipsState;
      if(this.dividerMobileFacets && this.dividerMobileFacets?.length > 0) {
        return chipsState
          .filter((filterNode) => {
              const nodeUniqueIdArray = filterNode.uniqueId.split(FILTERS_ID_SEPARATOR);
              return !(this.dividerMobileFacets.includes(nodeUniqueIdArray[0]) && nodeUniqueIdArray.length === 2);
          })
          .filter((filterNode) => {
              return filterNode.children.length === 0;
          }).length;
      }

      return chipsState.length;
  }

  private onOpenCloseFilters(event: CustomEvent) {
      const eventDetail: FiltersCollapseData | undefined = event.detail;
      const wantToClear: boolean = eventDetail && eventDetail?.clearSelected;
      const prevCollapsed = this.collapsed;

      if(wantToClear && this.countVisibleChips > 0) {
        this.collapsed = !this.collapsed;
        this.fixedView = this.onlyCollapse;
        const previewsFacetsData = cloneObjectWithNoReference(this.facetsData.previewsStateData);


        if(!this.onlyCollapse) {
          this.clear.emit({onlyCollapse: this.onlyCollapse, collapseState: this.collapsed});
          return;
        }

        if(this.onlyCollapse && previewsFacetsData.facetsChipsState.length > 0) {
          this.escape.emit();
          this.facetsDataProp = JSON.stringify(previewsFacetsData);
          this.facetsData = previewsFacetsData;
        }

        return;
      } else if(this.countVisibleChips > 0) {
        this.collapsed = true;
        this.fixedView = true;
        return;
      }

      this.collapsed = !this.collapsed;
      this.fixedView = this.collapsed;

      if(prevCollapsed) {
        this.clear.emit({onlyCollapse: this.onlyCollapse, collapseState: this.collapsed});
      }
      this.onlyCollapse = false;
  }

  private calculateTreeMenuHeight(height: number): void {
      const heightDiv = height + this.filtersResultButtonHeight;
      this.filtersTreeMenuElement.style.height = `calc(100vh - ${heightDiv}px)`;
  }

  private collapseAllNodes(): void {
      // @ts-ignore
      for(const element of this.filtersTreeMenuElement.querySelectorAll('filters-tree-menu filters-tree-node')) {
        element.collapseClose();
      }
  }
}
